import {
  fetchOrderHistory,
  type FetchOrderHistoryRequestData,
} from "~/features/orders/orderList/api/fetchOrderHistory/fetchOrderHistory";
import { map, merge, Observable } from "rxjs";
import { type DetailedOrderLineItemData, type OrderData, type QuoteData } from "~/domains/orders/types/modelsV2";
import { type OrderListSearchBoxData } from "~/features/orders/orderList/components/OrderListSearchBox/OrderListSearchBox";
import { QUERY_TYPE } from "~/features/orders/common/queryTypes";
import { fetchQuoteHistory } from "~/features/orders/quoteList/api/fetchQuoteHistory/fetchQuoteHistory";
import { groupByFn } from "~/common/utils/reducers";
import {
  type GroupedAccountsData,
  type SingleAccountData,
} from "~/features/orders/common/DownloadOrderHistoryService/types";
import {
  fetchOrderbookData,
  type OrderbookDataRequest,
  type OrderbookDataResponse,
} from "~/features/orderbook/api/fetchOrderbookData";
import { type OrderbookSearchBoxData } from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchBox";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";
import { format } from "~/common/utils/ApiHelper";

type SkipToken = string | undefined | null;

export interface PaginatedFetchFuncProps<T> {
  data: T;
  skipToken?: string;
  signal?: AbortSignal;
}

export interface PaginatedFetchFuncResponse<T> {
  data: T;
  nextSkipToken?: string;
}

function streamPaginatedDataFuncFactory<T, R>(
  fetchFunc: (props: PaginatedFetchFuncProps<T>) => Promise<PaginatedFetchFuncResponse<R>>,
) {
  return (data: T) =>
    new Observable<R>((subscriber) => {
      const abortController = new AbortController();

      (async () => {
        let skipToken: SkipToken = undefined;
        try {
          do {
            const response: PaginatedFetchFuncResponse<R> = await fetchFunc({
              data,
              skipToken,
              signal: abortController.signal,
            });
            skipToken = response.nextSkipToken;
            subscriber.next(response.data as R);
          } while (skipToken !== null && skipToken !== undefined);
          subscriber.complete();
        } catch (e) {
          console.error(e);
          subscriber.error(e);
        }
      })();

      return () => {
        abortController.abort();
      };
    });
}

const getOrderListSearchResultsForSingleCustomer = streamPaginatedDataFuncFactory(fetchOrderHistory);
const getQuoteListSearchResultsForSingleCustomer = streamPaginatedDataFuncFactory(fetchQuoteHistory);
const getOrderbookDataResultsForSingleCustomer = streamPaginatedDataFuncFactory(fetchOrderbookData);

export interface GetOrderListSearchResults extends Omit<FetchOrderHistoryRequestData, "account"> {
  accounts?: SingleAccountData[];
}

function splitRequests<T extends { accounts?: SingleAccountData[] }>(
  data: T,
): (Omit<T, "accounts"> & { account?: GroupedAccountsData })[] {
  const { accounts, ...rest } = data;
  let result;
  if (accounts && accounts.length > 0) {
    const transformedAccounts: { salesOrg: string; code: string; endCustomerCodes: string[] }[] = [];

    Object.entries(groupByFn(accounts, (acc) => acc.salesOrg)).forEach(([salesOrg, accounts]) => {
      Object.entries(groupByFn(accounts, (acc) => acc.accountNumber)).forEach(([code, accounts]) => {
        transformedAccounts.push({ salesOrg, code, endCustomerCodes: accounts.map((_) => _.endCustomer).filter(Boolean) });
      });
    });

    result = transformedAccounts.map((account) => ({
      ...rest,
      account,
    }));
  } else {
    result = [rest];
  }
  return result;
}

export function getOrderListSearchResults(data: GetOrderListSearchResults): Observable<OrderData[]> {
  const splitData = splitRequests(data);
  const obs = merge(...splitData.map((data) => getOrderListSearchResultsForSingleCustomer(data)));
  return obs.pipe(map((data) => data.results));
}

export function getQuoteListSearchResults(data: GetOrderListSearchResults): Observable<QuoteData[]> {
  const splitData = splitRequests(data);
  const obs = merge(...splitData.map((data) => getQuoteListSearchResultsForSingleCustomer(data)));
  return obs.pipe(map((data) => data.results));
}

interface OrderbookMultiAccountRequest extends Omit<OrderbookDataRequest, "account"> {
  accounts?: SingleAccountData[];
}

export function getOrderbookDataResults(data: OrderbookMultiAccountRequest): Observable<OrderbookDataResponse> {
  const splitData = splitRequests(data);
  const obs = merge(...splitData.map((data) => getOrderbookDataResultsForSingleCustomer(data)));
  return obs;
}

export function transformFormData(data: OrderListSearchBoxData): GetOrderListSearchResults {
  return {
    customerOrderId: data.queryType === QUERY_TYPE.PURCHASE_NO ? data.query : undefined,
    salesDocument: data.queryType === QUERY_TYPE.SALES_NO ? data.query : undefined,
    lineCustomerOrderId: data.queryType === QUERY_TYPE.CUSTOMER_ORDER_NO ? data.query : undefined,
    partNumber: data.queryType === QUERY_TYPE.DESIGNATION ? data.query : undefined,
    matnr: data.queryType === QUERY_TYPE.SAP_MATERIAL_NO ? data.query : undefined,
    customerMatnr: data.queryType === QUERY_TYPE.CUSTOMER_MATERIAL_NO ? data.query : undefined,
    invoicenr: data.queryType === QUERY_TYPE.INVOICE_NO ? data.query : undefined,
    sdCreatenEnd: data.dateTo ? format(data.dateTo) : undefined,
    sdCreationStart: data.dateFrom ? format(data.dateFrom) : undefined,
    sdLineStatuses: data.lineStatus ?? [],
    sdStatus: data.status ?? "",
    accounts: data.accounts,
  };
}

export function filterProducts<T extends DetailedOrderLineItemData>(
  products: T[],
  formData: GetOrderListSearchResults | null,
): T[] {
  if (formData?.matnr) {
    // when searched by 13 digit material number
    if (formData.matnr.length == 13) {
      return products.filter((orderLine) => formData.matnr?.startsWith(orderLine.product.sapMaterialNumber));
    }
    // when searched by 15 digit material number
    return products.filter((orderLine) => orderLine.product.sapMaterialNumber + orderLine.packCode === formData.matnr);
  } else if (formData?.partNumber) {
    // when searched by product designation
    return products.filter((orderLine) => orderLine.product.designation.startsWith(<string>formData.partNumber));
  }
  return products;
}

export function filterProductsMO(products: QuoteLineMO[], formData: GetOrderListSearchResults | null): QuoteLineMO[] {
  if (formData?.matnr) {
    // when searched by 13 digit material number
    if (formData.matnr.length == 13) {
      return products.filter((orderLine) => formData.matnr?.startsWith(orderLine.product.sapMaterialNumber));
    }
    // when searched by 15 digit material number
    return products.filter((orderLine) => orderLine.product.sapMaterialNumber + orderLine.packCode === formData.matnr);
  } else if (formData?.partNumber) {
    // when searched by product designation
    return products.filter((orderLine) => orderLine.product.designation.startsWith(<string>formData.partNumber));
  }
  return products;
}

export function transformOrderbookFormData(data: OrderbookSearchBoxData): OrderbookMultiAccountRequest {
  return data;
}
